import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode
} from 'react'
import { getCountryConfig, getDefaultCountryCode, isValidCountryCode } from '@/config/countries/registry'
import { formatCurrency as formatCurrencyUtil } from '@/lib/formatters/currency'
import type { CountryCode, CountryConfig, CurrencyConfig, AssetConfig, AssetTypeDefinition } from '@/config/countries/types'

// ===========================================
// CONTEXT INTERFACE
// ===========================================

interface CountryContextValue {
  // Current state
  countryCode: CountryCode
  config: CountryConfig

  // Actions
  setCountry: (code: CountryCode) => void

  // Convenience accessors
  currency: CurrencyConfig
  assets: AssetConfig
  taxRules: CountryConfig['taxRules']
  labels: CountryConfig['labels']
  helpText: CountryConfig['helpText']

  // Helpers
  formatCurrency: (amount: number, overrideCurrency?: CurrencyConfig) => string
  formatCurrencyCompact: (amount: number) => string
  getAssetConfig: (assetId: string) => AssetTypeDefinition | undefined
}

// ===========================================
// CONTEXT
// ===========================================

const CountryContext = createContext<CountryContextValue | null>(null)

const STORAGE_KEY = 'nri-fire-country'

// ===========================================
// PROVIDER
// ===========================================

export function CountryProvider({ children }: { children: ReactNode }) {
  const [countryCode, setCountryCode] = useState<CountryCode>(() => {
    if (typeof window === 'undefined') return getDefaultCountryCode()

    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && isValidCountryCode(stored)) {
      return stored
    }
    return getDefaultCountryCode()
  })

  const config = useMemo(() => getCountryConfig(countryCode), [countryCode])

  const setCountry = useCallback((code: CountryCode) => {
    setCountryCode(code)
    localStorage.setItem(STORAGE_KEY, code)
  }, [])

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, countryCode)
  }, [countryCode])

  // CRITICAL: Sync AppStore's countryCode with CountryContext on mount ONLY
  // This fixes desync issues after page reload that can cause Net Worth to show 0
  // Using a ref to ensure this only runs once on mount, not on every countryCode change
  const hasInitializedRef = useRef(false)
  useEffect(() => {
    if (hasInitializedRef.current) return
    hasInitializedRef.current = true

    // Dynamic import to avoid circular dependencies
    import('@/store/useAppStore').then(({ useAppStore }) => {
      const storeCountryCode = useAppStore.getState().residentCountryAssets.countryCode
      if (storeCountryCode !== countryCode) {
        // Sync AppStore to match CountryContext (the source of truth for UI)
        useAppStore.getState().setResidentCountry(countryCode)
      }
    })
  }, [countryCode])

  // Currency formatting helpers
  const formatCurrency = useCallback((
    amount: number,
    overrideCurrency?: CurrencyConfig
  ): string => {
    return formatCurrencyUtil(amount, overrideCurrency || config.currency)
  }, [config.currency])

  const formatCurrencyCompact = useCallback((amount: number): string => {
    return formatCurrencyUtil(amount, config.currency, { compact: true })
  }, [config.currency])

  // Asset config lookup helper
  const getAssetConfig = useCallback((assetId: string) => {
    for (const category of Object.values(config.assets)) {
      const found = category.find((a: AssetTypeDefinition) => a.id === assetId)
      if (found) return found
    }
    return undefined
  }, [config.assets])

  const value: CountryContextValue = useMemo(() => ({
    countryCode,
    config,
    setCountry,
    currency: config.currency,
    assets: config.assets,
    taxRules: config.taxRules,
    labels: config.labels,
    helpText: config.helpText,
    formatCurrency,
    formatCurrencyCompact,
    getAssetConfig,
  }), [
    countryCode,
    config,
    setCountry,
    formatCurrency,
    formatCurrencyCompact,
    getAssetConfig
  ])

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  )
}

// ===========================================
// HOOK
// ===========================================

export function useCountryConfig(): CountryContextValue {
  const context = useContext(CountryContext)
  if (!context) {
    throw new Error(
      'useCountryConfig must be used within CountryProvider. ' +
      'Wrap your app with <CountryProvider> in the component tree.'
    )
  }
  return context
}
